/**
 * The frame around everything: the wordmark, the connection state, and the theme switch.
 */

import { useEffect, useState } from 'react';

import type { ConnectionStatus } from '@gok/shared';

import { Badge } from './primitives.tsx';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'gok.theme';

export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'dark' || saved === 'light' ? saved : 'system';
    } catch {
      return 'system';
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);

    try {
      if (theme === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // A theme that cannot be remembered still applies for this visit.
    }
  }, [theme]);

  return [theme, setTheme];
}

export function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const next: Record<Theme, Theme> = {
    system: 'light',
    light: 'dark',
    dark: 'system',
  };
  const label: Record<Theme, string> = {
    system: 'Auto',
    light: 'Light',
    dark: 'Dark',
  };

  return (
    <button
      type='button'
      onClick={() => setTheme(next[theme])}
      className='rounded-md border border-rule px-2 py-1 font-mono text-[0.7rem] text-muted
        transition-colors hover:border-rule-strong hover:text-ink'
      aria-label={`Theme: ${label[theme]}. Click to change.`}
    >
      {label[theme]}
    </button>
  );
}

/**
 * Connection state, shown rather than hidden.
 *
 * The 2023 client reconnected its socket and never resubscribed, so a stale board looked
 * exactly like a live one. Whatever else this does, it must never be silent about being
 * out of sync.
 */
export function ConnectionBadge(
  { status, stale }: { status: ConnectionStatus; stale?: boolean },
) {
  if (status === 'ready' && !stale) return null;

  const text = status === 'ready'
    ? 'Resyncing'
    : status === 'offline'
    ? 'Offline'
    : 'Reconnecting';

  return (
    <span className='flex items-center gap-1.5'>
      <span
        className='size-1.5 animate-pulse rounded-full bg-alert'
        aria-hidden='true'
      />
      <Badge tone='alert'>{text}</Badge>
    </span>
  );
}

/** A standing link to the tutorial. The 2020 one was reachable only by URL. */
export function LearnLink() {
  return (
    <a
      href='#/learn'
      className='rounded-md px-2 py-1 text-sm text-ink-2 hover:bg-raised hover:text-ink'
    >
      How to play
    </a>
  );
}

/**
 * A standing link to the bot.
 *
 * Next to the tutorial rather than buried in the lobby, because the two answer the same
 * question — "can I try this without waiting for anybody" — and the answer should be
 * visible from every screen.
 */
export function BotLink() {
  return (
    <a
      href='#/play'
      className='rounded-md px-2 py-1 text-sm text-ink-2 hover:bg-raised hover:text-ink'
    >
      Play the bot
    </a>
  );
}

export function Wordmark() {
  return (
    <a
      href='#/'
      className='font-display text-[0.95rem] font-bold tracking-tight text-ink hover:text-accent'
    >
      {
        /* Explicit spaces: the formatter strips bare whitespace between JSX text and
          an element, which silently welds the wordmark into one word. */
      }
      Game <span className='text-muted'>of</span> Kings
    </a>
  );
}
